/**
 * Context for managing advisor access throughout the application
 * Provides advisor access data and utility functions with caching
 */

import BackendService from 'BackendService';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type {
  AdvisedFor,
  AdvisorAccess,
  AdvisorAccessContext,
  AdvisorRole,
} from '../models/Advisor';
import { adminAdvisorAccessService, advisorAccessService } from '../services/AdvisorAccessService';

interface AdvisorAccessProviderProps {
  children: ReactNode;
  isAdmin?: boolean;
}

export const AdvisorAccessContextInstance = createContext<AdvisorAccessContext | undefined>(
  undefined,
);

export const AdvisorAccessProvider: React.FC<AdvisorAccessProviderProps> = ({
  children,
  isAdmin = false,
}) => {
  const [advisorAccess, setAdvisorAccess] = useState<AdvisorAccess[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [advisedFor, setAdvisedFor] = useState<AdvisedFor | null>(null);

  const loadAdvisorAccess = useCallback(async () => {
    const service = isAdmin ? adminAdvisorAccessService : advisorAccessService;
    try {
      setLoading(true);
      const access = await service.getAdvisorAccess();
      setAdvisorAccess(access);
    } catch (err) {
      console.error('Failed to load advisor access:', err);
      setAdvisorAccess([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const refreshAdvisorAccess = async () => {
    const service = isAdmin ? adminAdvisorAccessService : advisorAccessService;
    try {
      setLoading(true);
      const access = await service.refreshAdvisorAccess();
      setAdvisorAccess(access);
    } catch (err) {
      console.error('Failed to refresh advisor access:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAdvisedFor = useCallback(async () => {
    try {
      const result = await new BackendService().getAdvisedFor();
      // Check if we got a special "no advisor context" response
      if (result && typeof result === 'object' && 'noAdvisorContext' in result) {
        // Redirect to /advice-view if no advisor context
        const currentPath = window.location.pathname;
        if (
          currentPath.startsWith('/advisor') &&
          !currentPath.startsWith('/advisors') &&
          currentPath !== '/advice-view'
        ) {
          window.location.href = '/advice-view';
          return;
        }
        setAdvisedFor(null);
        return;
      }
      setAdvisedFor(result);
    } catch (_e) {
      setAdvisedFor(null);
    }
  }, []);

  // Load advisor access and advised-for on mount
  useEffect(() => {
    loadAdvisorAccess();
    loadAdvisedFor();
  }, [loadAdvisorAccess, loadAdvisedFor]);

  const refreshAdvisedFor = async () => {
    await loadAdvisedFor();
  };

  const exitAdvisedFor = async () => {
    await new BackendService().exitAdvisedFor();
    await loadAdvisedFor();
  };

  const hasAdvisorAccess = (entityId?: string): boolean => {
    if (!advisorAccess) return false;

    if (!entityId) {
      return advisorAccess.length > 0;
    }

    return advisorAccess.some((advisorAccess) =>
      advisorAccess.entities.some((entity) => entity.id === entityId),
    );
  };

  const getAdvisorRole = (contactId: string, entityId?: string): AdvisorRole | null => {
    if (!advisorAccess) return null;

    // Find the advisor access item for the specific contact
    const advisorAccessItem = advisorAccess.find((a) => a.contactId === contactId);

    if (!advisorAccessItem) return null;

    // If entityId is provided, validate that the advisor has access to that entity for this contact
    if (entityId) {
      const hasEntityAccess = advisorAccessItem.entities.some((entity) => entity.id === entityId);
      if (!hasEntityAccess) return null;
    }

    // Return the role for this contact (and optionally entity)
    return advisorAccessItem.role;
  };

  const getAccessibleEntities = (): Array<{ id: string; name: string }> => {
    if (!advisorAccess) return [];

    const entities: Array<{ id: string; name: string }> = [];

    advisorAccess.forEach((advisorAccessItem) => {
      advisorAccessItem.entities.forEach((entity) => {
        if (!entities.find((e) => e.id === entity.id)) {
          entities.push(entity);
        }
      });
    });

    return entities;
  };

  const contextValue: AdvisorAccessContext = {
    advisorAccess,
    hasAdvisorAccess,
    getAdvisorRole,
    getAccessibleEntities,
    refreshAdvisorAccess,
    loading,
    advisedFor,
    refreshAdvisedFor,
    exitAdvisedFor,
  };

  return (
    <AdvisorAccessContextInstance.Provider value={contextValue}>
      {children}
    </AdvisorAccessContextInstance.Provider>
  );
};

export const useAdvisorAccess = (): AdvisorAccessContext => {
  const context = useContext(AdvisorAccessContextInstance);
  if (context === undefined) {
    throw new Error('useAdvisorAccess must be used within an AdvisorAccessProvider');
  }
  return context;
};

// no default export
